// src/utils/dateTime.ts
import moment from 'moment-timezone';

export type DisplayFormat = 'full' | 'short' | 'compact' | 'iso' | 'custom';

export interface DateTimeParts {
  date: Date;
  time: string; // "HH:mm"
  timezone: string; // IANA identifier
}

export interface FormatOptions {
  /** Custom Moment format string (when format="custom") */
  customFormat?: string;
  /** For "full" output, whether to append the tz abbreviation */
  showTimezone?: boolean;
  /** Override the detected locale (e.g. "fr-CA") */
  locale?: string;
}

/**
 * Formats a DateTimeParts object to a string, using either:
 * - the browser's locale (navigator.language) by default, or
 * - a provided override.
 */
export function formatDateTime(
  parts: DateTimeParts,
  format: DisplayFormat = 'full',
  options: FormatOptions = {}
): string {
  console.debug('[formatDateTime] entry:', { parts, format, options });

  const { date, time, timezone } = parts;
  if (!date || !time) {
    console.debug('[formatDateTime] missing date/time – returning empty string');
    return '';
  }

  const { customFormat, showTimezone = true, locale } = options;
  const userLocale = locale ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US');

  console.debug('[formatDateTime] using locale:', userLocale);

  // parse hours/minutes
  const [hours, minutes] = time.split(':').map(Number);
  console.debug('[formatDateTime] parsed time:', { hours, minutes });

  // build moment object
  const dt = moment(date).hours(hours).minutes(minutes).seconds(0).tz(timezone).locale(userLocale);

  console.debug('[formatDateTime] moment object:', {
    iso: dt.toISOString(),
    locale: dt.locale(),
    timezone: dt.tz(),
  });

  let result: string;
  switch (format) {
    case 'short': {
      result = dt.format('L LT');
      console.debug('[formatDateTime] [short] ->', result);
      return result;
    }

    case 'compact': {
      result = `${dt.format('L LT')} ${timezone}`;
      console.debug('[formatDateTime] [compact] ->', result);
      return result;
    }

    case 'iso': {
      result = dt.utc().format();
      console.debug('[formatDateTime] [iso] ->', result);
      return result;
    }

    case 'custom': {
      // 1️⃣ Normalize common lowercase mistakes
      const fmt = (customFormat || dt.localeData().longDateFormat('LL')).replace(/dd/g, 'DD').replace(/yy(?!yy)/g, 'YY');
      console.debug('[formatDateTime] [custom] date-only fmt:', fmt);

      // 2️⃣ Date part
      const datePart = dt.format(fmt);
      console.debug('[formatDateTime] [custom] datePart ->', datePart);

      // 3️⃣ Time + tz part (localized short time + zone abbr)
      const timeTzPart = dt.format('LT z');
      console.debug('[formatDateTime] [custom] timeTzPart ->', timeTzPart);

      // 4️⃣ Combine
      const combined = `${datePart} ${timeTzPart}`;
      console.debug('[formatDateTime] [custom] combined ->', combined);
      return combined;
    }

    case 'full':
    default: {
      const longFmt = dt.localeData().longDateFormat('LLLL');
      console.debug('[formatDateTime] [full] longFmt pattern:', longFmt);
      const base = dt.format(longFmt);
      console.debug('[formatDateTime] [full] base ->', base);
      if (showTimezone) {
        const tzAbbr = dt.format('z');
        result = `${base} ${tzAbbr}`;
        console.debug('[formatDateTime] [full] with tz ->', result);
      } else {
        result = base;
        console.debug('[formatDateTime] [full] no tz ->', result);
      }
      return result;
    }
  }
}

/**
 * Converts DateTimeParts to an ISO-8601 UTC string.
 */
export function toIsoString(parts: DateTimeParts): string {
  console.debug('[toIsoString] entry:', parts);

  const { date, time, timezone } = parts;
  const [hours, minutes] = time.split(':').map(Number);
  console.debug('[toIsoString] parsed time:', { hours, minutes });

  const dt = moment.tz(date, timezone).hour(hours).minute(minutes).second(0).millisecond(0);

  console.debug('[toIsoString] before UTC conversion:', dt.toISOString(), 'tz:', dt.tz());

  const iso = dt.utc().toISOString();
  console.debug('[toIsoString] result ISO UTC:', iso);

  return iso;
}